<template>
  <el-dialog
    v-model="dialogVisible"
    :title="geometryType === 'Point' ? '选择点位' : '勾选地块'"
    width="900px"
    @close="handleClose"
  >
    <div class="geometry-selector">
      <div class="map-toolbar">
        <el-button type="primary" @click="startDraw" :disabled="isDrawing">
          {{ geometryType === 'Point' ? '开始选择点位' : '开始勾选地块' }}
        </el-button>
        <el-button @click="clearGeometry" :disabled="!currentFeature">清除</el-button>
        <el-button @click="handleConfirm" :disabled="!currentFeature" type="success">确认</el-button>
      </div>
      <div ref="mapContainer" class="map-container"></div>
      <div class="geometry-info" v-if="currentWkt">
        <el-alert
          :title="`已${geometryType === 'Point' ? '选择点位' : '勾选地块'}（WKT格式）`"
          type="success"
          :closable="false"
        >
          <template #default>
            <div class="wkt-display">{{ currentWkt }}</div>
          </template>
        </el-alert>
      </div>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button type="primary" @click="handleConfirm" :disabled="!currentFeature">确定</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { ElMessage } from 'element-plus';
import * as KMap from '@/utils/ol-map/KMap';
import Draw from 'ol/interaction/Draw';
import WKT from 'ol/format/WKT';
import Feature from 'ol/Feature';
import config from '@/api/config.js';
import * as util from '@/common/ol_common.js';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  geometryType: {
    type: String,
    default: 'Polygon', // 'Point' 或 'Polygon'
    validator: (value) => ['Point', 'Polygon'].includes(value)
  },
  initialGeom: {
    type: String,
    default: ''
  },
  center: {
    type: String,
    default: 'POINT(113.61702297075017 23.584863449735067)' // 默认中心点
  }
});

const emit = defineEmits(['update:modelValue', 'confirm', 'close']);

const dialogVisible = ref(false);
const mapContainer = ref(null);
const isDrawing = ref(false);
const currentFeature = ref(null);
const currentWkt = ref('');
let kmap = null;
let draw = null;
let vectorLayer = null;

watch(() => props.modelValue, (val) => {
  dialogVisible.value = val;
  if (val) {
    nextTick(() => {
      initMap();
    });
  }
});

watch(dialogVisible, (val) => {
  if (val !== props.modelValue) {
    emit('update:modelValue', val);
  }
});

function initMap() {
  if (!mapContainer.value) {
    return;
  }

  // 初始化地图
  const level = 15;
  let coordinate;
  
  if (props.initialGeom) {
    // 如果有初始几何信息，使用它来确定中心点
    try {
      const geom = new WKT().readGeometry(props.initialGeom);
      const extent = geom.getExtent();
      coordinate = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
    } catch (e) {
      const centerGeom = util.wktCastGeom(props.center);
      coordinate = centerGeom.getFirstCoordinate();
    }
  } else {
    const centerGeom = util.wktCastGeom(props.center);
    coordinate = centerGeom.getFirstCoordinate();
  }

  kmap = new KMap.Map(mapContainer.value, level, coordinate[0], coordinate[1], null, 10, 22);
  
  // 添加底图
  const xyz2 = config.base_img_url3 + 'map/lby/{z}/{x}/{y}.png';
  kmap.addXYZLayer(xyz2, { minZoom: 12, maxZoom: 22 });

  // 创建矢量图层用于绘制
  const vectorStyle = new KMap.VectorStyle();
  const style = props.geometryType === 'Point' 
    ? (f) => vectorStyle.getPointSimpleStyle(8, '#409EFF', '#FFFFFF', 2)
    : (f) => vectorStyle.getPolygonStyle('#409EFF40', '#409EFF', 2);
  
  vectorLayer = new KMap.VectorLayer('drawLayer', 999, { style });
  kmap.addLayer(vectorLayer.layer);

  // 初始化绘制工具
  initDraw();

  // 如果有初始几何信息，在地图上显示
  if (props.initialGeom) {
    loadInitialGeometry();
  }
}

function initDraw() {
  if (!kmap || !vectorLayer) {
    return;
  }

  draw = new Draw({
    source: vectorLayer.source,
    type: props.geometryType,
    freehand: false,
    stopClick: true
  });

  draw.setActive(false);
  kmap.map.addInteraction(draw);

  draw.on('drawstart', () => {
    isDrawing.value = true;
    // 清除之前的图形
    if (currentFeature.value) {
      vectorLayer.removeFeature(currentFeature.value);
      currentFeature.value = null;
      currentWkt.value = '';
    }
  });

  draw.on('drawend', (e) => {
    isDrawing.value = false;
    draw.setActive(false);
    
    if (e.feature) {
      currentFeature.value = e.feature;
      vectorLayer.addFeature(e.feature);
      
      // 转换为 WKT 格式
      const wkt = new WKT().writeGeometry(e.feature.getGeometry());
      currentWkt.value = wkt;
    }
  });
}

function loadInitialGeometry() {
  if (!props.initialGeom || !vectorLayer) {
    return;
  }

  try {
    const geom = new WKT().readGeometry(props.initialGeom);
    const feature = new Feature({
      geometry: geom
    });
    currentFeature.value = feature;
    vectorLayer.addFeature(feature);
    currentWkt.value = props.initialGeom;
    
    // 调整地图视野到几何范围
    const extent = geom.getExtent();
    kmap.view.fit(extent, { padding: [50, 50, 50, 50], duration: 500 });
  } catch (error) {
    console.error('加载初始几何信息失败:', error);
  }
}

function startDraw() {
  if (draw) {
    draw.setActive(true);
  }
}

function clearGeometry() {
  if (currentFeature.value && vectorLayer) {
    vectorLayer.removeFeature(currentFeature.value);
    currentFeature.value = null;
    currentWkt.value = '';
  }
}

function handleConfirm() {
  if (currentWkt.value) {
    emit('confirm', currentWkt.value);
    handleClose();
  } else {
    ElMessage.warning('请先选择几何信息');
  }
}

function handleClose() {
  dialogVisible.value = false;
  emit('close');
  // 清理资源
  if (draw) {
    draw.setActive(false);
  }
  if (currentFeature.value && vectorLayer) {
    vectorLayer.removeFeature(currentFeature.value);
  }
  currentFeature.value = null;
  currentWkt.value = '';
}

onBeforeUnmount(() => {
  if (kmap && kmap.map) {
    kmap.map.setTarget(null);
  }
});
</script>

<style scoped>
.geometry-selector {
  display: flex;
  flex-direction: column;
  height: 600px;
}

.map-toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 4px;
}

.map-container {
  flex: 1;
  width: 100%;
  min-height: 500px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.geometry-info {
  margin-top: 10px;
}

.wkt-display {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  word-break: break-all;
  margin-top: 8px;
  padding: 8px;
  background: #f0f9ff;
  border-radius: 4px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>

